import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Ban, CheckCircle2, Trash2, ShieldCheck } from "lucide-react";

const EMPTY = { name: "", email: "", role: "pfi", password: "", active: true, price_region_id: "" };

export default function Users() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isSuperAdmin = user?.role === "super_admin";
  const [list, setList] = useState([]);
  const [regions, setRegions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  async function load() { const { data } = await api.get("/users"); setList(data); setSelected(new Set()); }
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    // Phase R4: dynamic role dropdown — fetch from DB (system + custom roles).
    // Fallback to the legacy hardcoded list if the request fails for any reason,
    // so the dialog never becomes unusable.
    api.get("/roles")
      .then((r) => setRoles(r.data.map((x) => x.slug).filter((s) => s !== "super_admin")))
      .catch(() => setRoles(["admin", "pfi", "accounts", "production", "dispatch", "management", "operations", "sales"]));
  }, []);

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }
  function toggleAll() { setSelected(selected.size === list.length ? new Set() : new Set(list.filter((u) => u.id !== user?.id).map((u) => u.id))); }

  function startEdit(u) { setForm({ ...u, password: "" }); setEditId(u.id); setOpen(true); }
  function startNew() { setForm(EMPTY); setEditId(null); setOpen(true); }

  async function save() {
    try {
      if (editId) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editId}`, payload);
      } else {
        // New user: include optional price_region_id so sales users created
        // here are immediately scoped without needing the Permissions editor.
        const payload = { ...form };
        if (!payload.price_region_id) delete payload.price_region_id;
        await api.post("/users", payload);
      }
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function quickToggle(u) {
    try {
      const path = u.active ? `/users/${u.id}/deactivate` : `/users/${u.id}/activate`;
      await api.post(path); toast.success(u.active ? "User deactivated" : "User activated"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} user(s)?`)) return;
    try {
      const { data } = await api.post("/users/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} user(s) deleted`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Users</h1>
          <p className="text-sm text-slate-500 mt-1">Internal accounts. Deactivate immediately revokes access on next request.</p>
        </div>
        <div className="flex gap-2">
          {selected.size > 0 && <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="users-bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size}</Button>}
          <Button onClick={startNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-user-btn"><Plus className="h-4 w-4 mr-1.5" /> New User</Button>
        </div>
      </div>
      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="p-3.5 w-8"><Checkbox checked={list.length > 0 && selected.size > 0 && selected.size === list.filter((u) => u.id !== user?.id).length} onCheckedChange={toggleAll} data-testid="users-select-all" /></th>
                <th className="p-3.5 text-left">Name</th>
                <th className="p-3.5 text-left">Email</th>
                <th className="p-3.5 text-left">Role</th>
                <th className="p-3.5 text-left">Status</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((u) => (
                <tr key={u.id} className="table-row-hover">
                  <td className="p-3.5">{u.id !== user?.id && <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`users-sel-${u.id}`} />}</td>
                  <td className="p-3.5 font-medium text-slate-900">{u.name}</td>
                  <td className="p-3.5 text-slate-700">{u.email}</td>
                  <td className="p-3.5"><span className="text-[11px] px-2 py-0.5 rounded-full border bg-slate-100 text-slate-700 border-slate-200 uppercase tracking-wide">{u.role}</span></td>
                  <td className="p-3.5">{u.active ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Active</span> : <span className="text-[11px] px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">Blocked</span>}</td>
                  <td className="p-3.5 text-right">
                    <div className="flex gap-1.5 justify-end">
                      {isSuperAdmin && (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-slate-700 border-slate-300 hover:bg-slate-50" onClick={() => nav(`/users/permissions/${u.id}`)} data-testid={`permissions-user-${u.id}`}><ShieldCheck className="h-3 w-3 mr-1" /> Permissions</Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                      {u.active ? (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => quickToggle(u)} data-testid={`deactivate-user-${u.id}`}><Ban className="h-3 w-3 mr-1" /> Block</Button>
                      ) : (
                        <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => quickToggle(u)} data-testid={`activate-user-${u.id}`}><CheckCircle2 className="h-3 w-3 mr-1" /> Activate</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="heading">{editId ? "Edit" : "New"} User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} disabled={!!editId} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@kaldan.co" /></div>
            <div><Label>Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r} value={r} data-testid={`user-role-option-${r}`}>{r}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>{editId ? "Reset Password (optional)" : "Password"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            {!editId && (
              <div>
                <Label>Price List Region <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Select value={form.price_region_id || "__NONE__"} onValueChange={(v) => setForm({ ...form, price_region_id: v === "__NONE__" ? "" : v })}>
                  <SelectTrigger data-testid="new-user-price-region"><SelectValue placeholder="— Not assigned —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__NONE__">— Not assigned —</SelectItem>
                    {regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Scopes the user's price list and (for sales role) default region. Can be changed later in Permissions.</p>
              </div>
            )}
            <div className="flex items-center gap-2"><Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-user">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
